import React, { useState, useEffect } from 'react';
import {
    Card,
    Table,
    Typography,
    Tag,
    Input,
    Space,
    Button,
    message,
    Row,
    Col,
    Modal,
} from 'antd';
import {
    SearchOutlined,
    FileTextOutlined,
    ReloadOutlined,
    DownloadOutlined,
    CrownFilled,
    InfoCircleOutlined,
} from '@ant-design/icons';
import * as XLSX from 'xlsx';
import AuthService from '../../services/authService';
import ProtectedMenuPage from '../../components/ProtectedMenuPage/ProtectedMenuPage';
import './PaymentHistory.css';

const { Title, Text } = Typography;

const calculateDday = dateStr => {
    if (!dateStr) return '';
    const targetDate = new Date(dateStr);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    targetDate.setHours(0, 0, 0, 0);

    const diffTime = targetDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'D-Day';
    if (diffDays > 0) return `D-${diffDays}`;
    return `D+${Math.abs(diffDays)}`;
};

export const PaymentHistory = () => {
    const [loading, setLoading] = useState(false);
    const [payments, setPayments] = useState([]);
    const [subscription, setSubscription] = useState(null);
    const [searchText, setSearchText] = useState('');

    useEffect(() => {
        fetchHistory();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const fetchHistory = async (isReset = false) => {
        setLoading(true);
        try {
            let url = '/payments/history';
            const params = new URLSearchParams();
            if (!isReset && searchText) {
                params.append('search', searchText);
            }
            if (params.toString()) {
                url += `?${params.toString()}`;
            }

            const response = await AuthService.authAxios.get(url);
            if (response.data.success) {
                setPayments(response.data.data.payments || []);
                setSubscription(response.data.data.subscription || null);
            }
        } catch (error) {
            console.error('Failed to fetch payment history:', error);
            message.error('결제 내역을 불러오는데 실패했습니다.');
        } finally {
            setLoading(false);
        }
    };

    const handleReset = () => {
        setSearchText('');
        fetchHistory(true);
    };

    const fetchAndOpenReceipt = async paymentKey => {
        try {
            const response = await AuthService.authAxios.get(`/payments/receipt-url/${paymentKey}`);
            if (response.data.success && response.data.url) {
                window.open(
                    response.data.url,
                    'receiptPopup',
                    'width=450,height=700,left=200,top=100,scrollbars=yes'
                );
            } else {
                message.warning(response.data.message || '영수증 정보를 확인할 수 없습니다.');
            }
        } catch (err) {
            console.error('영수증 조회 오류:', err);
            message.error('영수증을 불러오는 중 오류가 발생했습니다.');
        }
    };

    const handleExcelDownload = () => {
        if (!payments || payments.length === 0) {
            message.warning('다운로드할 데이터가 없습니다.');
            return;
        }
        const ws = XLSX.utils.json_to_sheet(
            payments.map(p => ({
                '결제 일시': p.approved_at ? new Date(p.approved_at).toLocaleString() : '-',
                '주문 번호': p.order_id,
                구분: p.payment_type === 'BILLING' ? '정기결제' : '단건결제',
                '결제 수단': p.method || '카드',
                '결제 금액': Number(p.amount),
                상태:
                    p.status === 'DONE'
                        ? '결제완료'
                        : p.status === 'CANCELED'
                          ? '결제취소'
                          : p.status === 'PARTIAL_CANCELED'
                            ? '부분취소'
                            : p.status,
            }))
        );
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, '결제내역');
        XLSX.writeFile(wb, '결제내역.xlsx');
    };

    const handleTestReset = () => {
        Modal.confirm({
            title: '결제/구독 데이터 초기화 (테스트용)',
            content:
                '정말 모든 결제 내역과 구독 정보를 삭제하고 등급을 user로 되돌리시겠습니까? 이 작업은 되돌릴 수 없습니다.',
            okText: '초기화 실행',
            okType: 'danger',
            cancelText: '취소',
            onOk: async () => {
                try {
                    const res = await AuthService.authAxios.post('/payments/test-reset');
                    if (res.data.success) {
                        message.success(
                            '모든 데이터가 초기화되었습니다. 권한 갱신을 위해 다시 로그인해주세요.'
                        );
                        setTimeout(() => {
                            AuthService.logout();
                            window.location.href = '/login';
                        }, 1500);
                    }
                } catch (error) {
                    message.error('초기화 실패');
                }
            },
        });
    };

    const handleCancelSubscription = () => {
        Modal.confirm({
            title: '정기구독 해지',
            icon: null,
            content: (
                <div style={{ marginTop: '16px' }}>
                    <p
                        style={{
                            fontSize: '15px',
                            color: '#262626',
                            marginBottom: '16px',
                            fontWeight: 500,
                        }}
                    >
                        정말{' '}
                        {subscription?.grade_name
                            ? subscription.grade_name.toUpperCase()
                            : 'Premium'}{' '}
                        정기구독을 해지하시겠습니까?
                    </p>
                    <div
                        style={{
                            background: '#f5f5f5',
                            padding: '12px 16px',
                            borderRadius: '8px',
                            fontSize: '13.5px',
                            color: '#595959',
                            display: 'flex',
                            gap: '8px',
                            alignItems: 'flex-start',
                            lineHeight: '1.5',
                        }}
                    >
                        <InfoCircleOutlined style={{ color: '#8c8c8c', marginTop: '3px' }} />
                        <div>
                            해지하더라도 결제된 기간인{' '}
                            <strong style={{ color: '#1677ff' }}>
                                {subscription?.next_payment_date
                                    ? (() => {
                                          const d = new Date(subscription.next_payment_date);
                                          const dday = calculateDday(
                                              subscription.next_payment_date
                                          );
                                          return `${d.getFullYear()}년 ${d.getMonth() + 1}월 ${d.getDate()}일 (${dday})`;
                                      })()
                                    : ''}
                                까지
                            </strong>
                            는 계속해서 유료 기능을 이용하실 수 있습니다.
                        </div>
                    </div>
                </div>
            ),
            okText: '해지하기',
            okType: 'danger',
            cancelText: '유지하기',
            onOk: async () => {
                try {
                    const res = await AuthService.authAxios.post('/payments/billing/cancel');
                    if (res.data.success) {
                        message.success(res.data.message || '정기구독이 해지되었습니다.');
                        fetchHistory();
                    } else {
                        message.error(res.data.message || '해지 처리에 실패했습니다.');
                    }
                } catch (error) {
                    console.error('Subscription cancel error:', error);
                    message.error(
                        error.response?.data?.message || '구독 해지 중 오류가 발생했습니다.'
                    );
                }
            },
        });
    };

    const columns = [
        {
            title: '결제 일시',
            dataIndex: 'approved_at',
            key: 'approved_at',
            render: (text, record) => {
                if (text) return new Date(text).toLocaleString();
                // 무통장 입금 승인완료된 경우, 승인 시간을 DB에 넣지 않았더라도 요청일(created_at)을 노출
                if (record.status === 'DONE' && record.created_at) {
                    return new Date(record.created_at).toLocaleString();
                }
                return '-';
            },
        },
        {
            title: '이용 기간',
            key: 'valid_period',
            render: (_, record) => {
                if (record.status !== 'DONE') return '-';

                let start, end;
                let parsedInfo = {};
                try {
                    parsedInfo =
                        typeof record.tax_invoice_info === 'string'
                            ? JSON.parse(record.tax_invoice_info)
                            : record.tax_invoice_info || {};
                } catch (e) {
                    console.error('Failed to parse tax_invoice_info:', e);
                }

                // 무통장 입금처럼 수동으로 승인된 기간이 있다면 우선 사용
                if (parsedInfo.approvedStartDate && parsedInfo.approvedEndDate) {
                    start = new Date(parsedInfo.approvedStartDate);
                    end = new Date(parsedInfo.approvedEndDate);
                } else if (record.approved_at || record.created_at) {
                    // 일반 카드 결제 등의 기본 30일 로직 (과거 내역 호환 포함)
                    start = new Date(record.approved_at || record.created_at);
                    end = new Date(start);
                    end.setDate(end.getDate() + 30);
                } else {
                    return '-';
                }

                const format = d => {
                    const y = d.getFullYear();
                    const m = String(d.getMonth() + 1).padStart(2, '0');
                    const day = String(d.getDate()).padStart(2, '0');
                    return `${y}-${m}-${day}`;
                };
                return <Text type="secondary">{`${format(start)} ~ ${format(end)}`}</Text>;
            },
        },
        {
            title: '주문 번호',
            dataIndex: 'order_id',
            key: 'order_id',
        },
        {
            title: '구분',
            dataIndex: 'payment_type',
            key: 'payment_type',
            render: type => (
                <Tag color={type === 'BILLING' ? 'purple' : 'blue'}>
                    {type === 'BILLING' ? '정기결제' : '단건결제'}
                </Tag>
            ),
        },
        {
            title: '결제 수단',
            dataIndex: 'method',
            key: 'method',
            render: text => <Text>{text || '카드'}</Text>,
        },
        {
            title: '결제 금액',
            dataIndex: 'amount',
            key: 'amount',
            render: amount => <Text strong>{Number(amount).toLocaleString()}원</Text>,
        },
        {
            title: '상태',
            dataIndex: 'status',
            key: 'status',
            render: status => {
                let color = 'default';
                let text = status;
                if (status === 'DONE') {
                    color = 'success';
                    text = '결제완료';
                }
                if (status === 'CANCELED') {
                    color = 'error';
                    text = '결제취소';
                }
                if (status === 'PARTIAL_CANCELED') {
                    color = 'warning';
                    text = '부분취소';
                }
                if (status === 'REJECTED') {
                    color = 'error';
                    text = '결제거절(반려)';
                }
                if (status === 'WIRE_TRANSFER_PENDING') {
                    color = 'warning';
                    text = '승인대기';
                }
                return <Tag color={color}>{text}</Tag>;
            },
        },
        {
            title: '매출전표',
            key: 'receipt',
            render: (_, record) => {
                if (record.method === '무통장입금') return <Text type="secondary">-</Text>;
                return (
                    <Button
                        type="link"
                        size="small"
                        icon={<FileTextOutlined />}
                        onClick={() => {
                            if (record.receipt_url) {
                                window.open(
                                    record.receipt_url,
                                    'receiptPopup',
                                    'width=450,height=700,left=200,top=100,scrollbars=yes'
                                );
                            } else if (record.payment_key) {
                                fetchAndOpenReceipt(record.payment_key);
                            } else {
                                message.warning('영수증 정보를 확인할 수 없습니다.');
                            }
                        }}
                    >
                        영수증 보기
                    </Button>
                );
            },
        },
    ];

    return (
        <ProtectedMenuPage menuKey="paymentHistory">
            <div className="payment-history-page">
            <div style={{ padding: '14px 20px', maxWidth: '1200px', margin: '0 auto' }}>
                {subscription && (
                    <div
                        style={{
                            background:
                                subscription.status === 'CANCELED'
                                    ? '#fafafa'
                                    : 'linear-gradient(135deg, #e6f4ff 0%, #ffffff 100%)',
                            border:
                                subscription.status === 'CANCELED'
                                    ? '1px solid #d9d9d9'
                                    : '1px solid #91caff',
                            borderRadius: '16px',
                            padding: '16px 20px',
                            marginBottom: '16px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            boxShadow:
                                subscription.status === 'CANCELED'
                                    ? 'none'
                                    : '0 4px 12px rgba(22,119,255,0.08)',
                            flexWrap: 'wrap',
                            gap: '20px',
                        }}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                            <div
                                style={{
                                    width: '56px',
                                    height: '56px',
                                    borderRadius: '50%',
                                    background:
                                        subscription.status === 'CANCELED' ? '#bfbfbf' : '#1677ff',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    boxShadow:
                                        subscription.status === 'CANCELED'
                                            ? 'none'
                                            : '0 4px 10px rgba(22,119,255,0.3)',
                                }}
                            >
                                <CrownFilled style={{ fontSize: '28px', color: '#fff' }} />
                            </div>
                            <div>
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px',
                                        marginBottom: '4px',
                                    }}
                                >
                                    <Title
                                        level={4}
                                        style={{
                                            margin: 0,
                                            color:
                                                subscription.status === 'CANCELED'
                                                    ? '#595959'
                                                    : '#262626',
                                            fontWeight: 800,
                                        }}
                                    >
                                        {subscription.status === 'CANCELED'
                                            ? `${subscription?.grade_name ? subscription.grade_name.toUpperCase() : 'Premium'} 정기구독 해지됨`
                                            : `${subscription?.grade_name ? subscription.grade_name.toUpperCase() : 'Premium'} 정기구독 이용 중`}
                                    </Title>
                                    {subscription.status === 'CANCELED' ? (
                                        <Tag
                                            color="default"
                                            style={{
                                                borderRadius: '4px',
                                                margin: 0,
                                                fontWeight: 'bold',
                                            }}
                                        >
                                            해지 완료
                                        </Tag>
                                    ) : (
                                        <Tag
                                            color="blue"
                                            style={{
                                                borderRadius: '4px',
                                                margin: 0,
                                                fontWeight: 'bold',
                                            }}
                                        >
                                            매월 정기결제
                                        </Tag>
                                    )}
                                </div>
                                <Text style={{ color: '#595959', fontSize: '15px' }}>
                                    {subscription.status === 'CANCELED' ? (
                                        <span>
                                            남은 기간 동안 유료 기능을 사용할 수 있으며,{' '}
                                            <strong style={{ color: '#cf1322' }}>
                                                추가 결제는 진행되지 않습니다.
                                            </strong>
                                        </span>
                                    ) : (
                                        <span>
                                            매월{' '}
                                            <strong style={{ color: '#1677ff' }}>
                                                {Number(subscription.amount).toLocaleString()}원
                                            </strong>
                                            <span style={{ fontSize: '12px', color: '#8c8c8c', marginLeft: '4px' }}>
                                                (VAT 10% 별도)
                                            </span>
                                            이 결제되며, 해당 등급의 기능을 무제한으로 사용하실 수
                                            있습니다.
                                        </span>
                                    )}
                                </Text>
                            </div>
                        </div>

                        <div
                            style={{
                                background: '#ffffff',
                                padding: '16px 24px',
                                borderRadius: '12px',
                                border:
                                    subscription.status === 'CANCELED'
                                        ? '1px solid #d9d9d9'
                                        : '1px solid #bae0ff',
                                textAlign: 'right',
                                minWidth: '220px',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.02)',
                            }}
                        >
                            <Text
                                style={{
                                    fontSize: '13px',
                                    color: '#8c8c8c',
                                    display: 'block',
                                    marginBottom: '4px',
                                }}
                            >
                                {subscription.status === 'CANCELED'
                                    ? '이용 가능 기간'
                                    : '다음 결제 예정일'}
                            </Text>
                            <Text
                                style={{
                                    fontSize: '22px',
                                    color:
                                        subscription.status === 'CANCELED' ? '#8c8c8c' : '#262626',
                                    fontWeight: 800,
                                }}
                            >
                                {(() => {
                                    const d = new Date(subscription.next_payment_date);
                                    const dday = calculateDday(subscription.next_payment_date);
                                    return (
                                        <>
                                            {`${d.getFullYear()}년 ${d.getMonth() + 1}월 ${d.getDate()}일`}
                                            <span
                                                style={{
                                                    fontSize: '15px',
                                                    marginLeft: '8px',
                                                    color:
                                                        subscription.status === 'CANCELED'
                                                            ? '#cf1322'
                                                            : '#fa8c16',
                                                    fontWeight: 700,
                                                    background:
                                                        subscription.status === 'CANCELED'
                                                            ? '#fff1f0'
                                                            : '#fff7e6',
                                                    padding: '2px 8px',
                                                    borderRadius: '4px',
                                                    verticalAlign: 'middle',
                                                }}
                                            >
                                                {dday}
                                            </span>
                                        </>
                                    );
                                })()}
                            </Text>
                            {subscription.status !== 'CANCELED' && (
                                <div style={{ marginTop: '12px' }}>
                                    <Button
                                        type="default"
                                        danger
                                        size="middle"
                                        style={{
                                            borderRadius: '6px',
                                            fontSize: '13px',
                                            fontWeight: 600,
                                            padding: '4px 16px',
                                            height: '32px',
                                        }}
                                        onClick={handleCancelSubscription}
                                    >
                                        구독 해지하기
                                    </Button>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                <Card
                    bordered={false}
                    style={{ borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}
                >
                    <Row justify="space-between" align="middle" style={{ marginBottom: 12 }}>
                        <Col>
                            <Space size="small" wrap>
                                <Input
                                    placeholder="주문 번호 검색"
                                    prefix={<SearchOutlined />}
                                    value={searchText}
                                    onChange={e => setSearchText(e.target.value)}
                                    onPressEnter={() => fetchHistory(false)}
                                    style={{ width: '180px', fontSize: 12 }}
                                />
                                <Button
                                    type="primary"
                                    icon={<SearchOutlined />}
                                    onClick={() => fetchHistory(false)}
                                    loading={loading}
                                >
                                    조회 시작
                                </Button>
                                <Button icon={<ReloadOutlined />} onClick={handleReset}>
                                    초기화
                                </Button>
                            </Space>
                        </Col>
                        <Col>
                            <Button
                                className="excel-button"
                                icon={<DownloadOutlined />}
                                onClick={handleExcelDownload}
                                title="엑셀 다운로드"
                            />
                        </Col>
                    </Row>

                    <Table
                        columns={columns}
                        dataSource={payments}
                        rowKey="id"
                        loading={loading}
                        size="small"
                        pagination={{
                            pageSize: 10,
                            showSizeChanger: true,
                            showTotal: total => `총 ${total}건`,
                        }}
                        scroll={{ x: 'max-content' }}
                    />
                </Card>
            </div>
            </div>
        </ProtectedMenuPage>
    );
};

export default PaymentHistory;
